import { HttpClient } from '@angular/common/http';
import { computed, inject, Service, signal } from '@angular/core';
import { EMPTY, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { API } from '../api';
import { LogCat, LogLevel } from '../models/logger';
import { tapLogError } from '../utils/helpers.utils';
import { AuthService } from './auth.service';
import { LoggerService } from './logger.service';
import { SettingsService } from './settings.service';

const TERMS_ACCEPTANCE_PENDING_WINDOW_MS = 12 * 60 * 60 * 1000;

/**
 * Consolidated Terms & Conditions acceptance service.
 * Order of precedence for read:
 * 1. Backend-provided accountInfo.termsAccepted (once available)
 * 2. Local signal persisted to localStorage (pre-backend)
 *
 * accept()/decline() only affect the local fallback (until backend persistence exists).
 */
@Service()
export class TermsService {
	private auth = inject(AuthService);
	private logger = inject(LoggerService);
	private http = inject(HttpClient);
	private settings = inject(SettingsService);

	public consent = computed(() => this.auth.accountInfo()?.consent);
	public purposeIds = computed(() => Object.keys(this.consent()?.purpose_checks || {}));

	private acceptancePollingRequested = signal(false);

	private grantedOverride = computed(() => {
		if (environment.appMeta.environment === 'PROD') {
			return null;
		}
		const override = this.settings.value().termsGrantedOverride;
		return typeof override === 'boolean' ? override : null;
	});

	public termsAccepted = computed(() => {
		const override = this.grantedOverride();
		if (typeof override === 'boolean') {
			return override;
		}
		return !!this.auth.accountInfo()?.consent?.granted;
	});

	public accept(purposes: string[]) {
		if (!purposes.length) return EMPTY;

		this.logger.log('Accepting terms', LogCat.terms, { level: LogLevel.info });
		const identifier = this.auth.accountInfo()?.zeissIdAccountId;

		if (!identifier) return EMPTY;

		return this.http
			.post(`${API.base}${API.account.purposes}`, {
				identifier: identifier ? identifier.toString() : '',
				purposes: purposes.map((id) => ({ id, transactionType: 'CONFIRMED' })),
			})
			.pipe(
				tap(() => this.logger.log('Terms accepted', LogCat.terms, { level: LogLevel.info })),
				tapLogError(this.logger, LogCat.terms),
			);
	}

	public withdrawAllTerms() {
		const purposes = Object.keys(this.auth.accountInfo()?.consent?.purpose_checks || {});

		if (!purposes.length) return EMPTY;

		this.logger.log('Withdrawing terms', LogCat.terms, { level: LogLevel.info });
		const identifier = this.auth.accountInfo()?.zeissIdAccountId;

		return this.http
			.post(`${API.base}${API.account.purposes}`, {
				identifier: identifier ? identifier.toString() : '',
				purposes: purposes.map((id) => ({ id, transactionType: 'WITHDRAWN' })),
			})
			.pipe(
				tap(() => this.logger.log('Terms withdrawn', LogCat.terms, { level: LogLevel.info })),
				tap(() => this.clearAcceptanceTimestamp()),
				tapLogError(this.logger, LogCat.terms),
			);
	}

	public decline(): void {
		this.clearAcceptanceTimestamp();
		this.logger.log('Terms declined', LogCat.terms, { level: LogLevel.info });
	}

	public recordAcceptanceTimestamp(timestamp = Date.now()): void {
		this.settings.update({ termsAcceptanceTimestamp: timestamp });
	}

	public clearAcceptanceTimestamp(): void {
		this.clearAcceptancePollingRequest();
		this.settings.update({ termsAcceptanceTimestamp: null });
	}

	public isAcceptancePending(referenceTime = Date.now()): boolean {
		const timestamp = this.settings.value().termsAcceptanceTimestamp;
		if (typeof timestamp !== 'number') return false;
		if (referenceTime - timestamp > TERMS_ACCEPTANCE_PENDING_WINDOW_MS) {
			this.clearAcceptanceTimestamp();
			return false;
		}
		return true;
	}

	public requestAcceptancePolling(): void {
		this.acceptancePollingRequested.set(true);
	}

	public clearAcceptancePollingRequest(): void {
		this.acceptancePollingRequested.set(false);
	}

	public shouldPollAcceptance(): boolean {
		return this.acceptancePollingRequested();
	}

	public setGrantedOverride(override: boolean | null): void {
		if (environment.appMeta.environment === 'PROD') return;
		this.settings.update({ termsGrantedOverride: override });
	}
}
